/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { forwardRef, type ForwardedRef } from "react";

import { useFormat } from "@superweb/intl";
import { useTypo } from "@superweb/ui";

import type { FlatItem, NumberColumn } from "../types";
import { Cell } from "./cells";

export const NumberCell = forwardRef(
  <D extends Object>(
    {
      column,
      row,
      rowIndex,
      isHovered = false,
      isStickyFirstColumn = false,
      stickyFirstColumnShift = 0,
      index,
      tabIndex,
      onPress,
      updateFocusPointer,
      variant = "fill",
      position,
      isTopPinned = false,
      isBottomPinned = false,
      isLastRow = false,
    }: {
      index: number;
      tabIndex?: number;
      column: NumberColumn<D>;
      row: FlatItem<D>;
      rowIndex: number;
      onPress?: () => void;
      isHovered?: boolean;
      isTopPinned?: boolean;
      isBottomPinned?: boolean;
      isStickyFirstColumn?: boolean;
      stickyFirstColumnShift?: number;
      updateFocusPointer: () => void;
      variant?: "fill" | "ghost";
      isLastRow?: boolean;
      position: {
        firstLeft?: boolean;
        firstRight?: boolean;
        lastLeft?: boolean;
        lastRight?: boolean;
      };
    },
    ref: ForwardedRef<HTMLTableCellElement>,
  ) => {
    const typo = useTypo();
    const {
      formatInteger,
      formatDecimal,
      formatPercent,
      formatCurrency,
      formatUnit,
    } = useFormat();
    const value = column.value(row, rowIndex);

    let renderValue = undefined;
    if (value !== undefined) {
      switch (column.type) {
        case "integer":
          renderValue = formatInteger(value);
          break;
        case "percent":
          renderValue = formatPercent(value, {
            fractionDigits: 2,
            fractionDisplay: "always",
            ...column.format,
          });
          break;
        case "currency":
          renderValue = formatCurrency(value, column.currency, {
            symbol: false,
            ...column.format,
          });
          break;
        case "unit":
          renderValue = formatUnit(value, column.unit, {
            fractionDigits: 2,
            fractionDisplay: "always",
            symbol: false,
            ...column.format,
          });
          break;
        case "decimal":
        default:
          renderValue = formatDecimal(value, {
            fractionDigits: 2,
            fractionDisplay: "always",
            ...column.format,
          });
          break;
      }
    }

    return (
      <Cell
        ref={ref}
        index={index}
        tabIndex={tabIndex}
        onPress={onPress}
        isHovered={isHovered}
        isStickyFirstColumn={isStickyFirstColumn}
        stickyFirstColumnShift={stickyFirstColumnShift}
        updateFocusPointer={updateFocusPointer}
        variant={variant}
        align={"end"}
        position={position}
        isTopPinned={isTopPinned}
        isBottomPinned={isBottomPinned}
        isLastRow={isLastRow}
      >
        <span
          css={{
            ...typo({
              level: "body2",
              density: "tight",
              weight: "regular",
              tabularNumbers: true,
            }),
          }}
        >
          {renderValue}
        </span>
      </Cell>
    );
  },
);
